import { format, addYears } from 'date-fns'
import { asCurrency, asDate, asPercentage, getInvestment, updateValuesFromFields, validateFields } from '../util'
import { useAppState } from '../Context'
import { InvestmentChart } from '../components/Charts'
import { InvestmentScheduleTable } from '../components/Tables'
import { InputFields } from '../components/Fields'
import CalculateResults from '../components/CalculateResults'
import CalculateRow from '../components/CalculateRow'

function PanelInput() {
  return <InputFields />
}

function PanelCalculate() {
  const { values } = useAppState()

  return (
    <CalculateResults>
      <CalculateRow>
        <div className="richtext wysiwyg">
          <p>
            This calculator is designed to show you the value of an initial deposit at some point in the future. It
            assumes that the funds grow for the specified period of time.
          </p>
          <p className="font-bold">
            If you deposit {asCurrency(values.investment.presentValue)} on {asDate(values.investment.presentDate)} and
            let it compound {values.investment.frequency} until {asDate(values.investment.futureDate)} at{' '}
            {asPercentage(values.investment.interestRate)} interest, at the end of that time you will have saved{' '}
            {asCurrency(values.investment.futureValue)}.
          </p>
          <p>
            This might be the amount needed to fund a child's education, a 25th wedding anniversary trip, or your
            retirement.
          </p>
          <p>
            The Chart shows the year-by-year value in the fund created by the initial lump sum, based on the information
            input.
          </p>
          <p>
            The Detail page shows the year-by-year earnings amount along with the cumulative earnings, based on the
            inputs.
          </p>
        </div>
      </CalculateRow>
    </CalculateResults>
  )
}

function PanelChart() {
  const { values } = useAppState()

  return (
    <div className="py-4 space-y-4">
      <InvestmentChart investment={values.investment} title="Future Value of Your Money" />
    </div>
  )
}

function PanelDetail() {
  const { values } = useAppState()

  return <InvestmentScheduleTable investment={values.investment} />
}

export default {
  name: 'Future value calculator',
  tabs: [
    {
      name: 'Input',
      component: PanelInput,
      beforeTabChange: (dispatch, fields, values) => {
        const validate = validateFields(fields)

        if (validate.isValid) {
          const updatedValues = updateValuesFromFields(values, fields)
          const opts = {
            presentDate: updatedValues.presentDate,
            futureDate: updatedValues.futureDate,
            presentValue: updatedValues.presentValue,
            interestRate: updatedValues.interestRate,
            frequency: updatedValues.frequency,
          }
          const investment = getInvestment(opts)

          dispatch({ type: 'setValues', payload: { ...updatedValues } })
          dispatch({ type: 'setValue', payload: { name: 'investment', value: investment } })
        }

        dispatch({ type: 'setFields', payload: validate.fields })

        return validate.isValid
      },
    },
    {
      name: 'Calculate',
      component: PanelCalculate,
    },
    {
      name: 'Chart',
      component: PanelChart,
    },
    {
      name: 'Detail',
      component: PanelDetail,
    },
  ],
  fields: [
    {
      type: 'date',
      name: 'presentDate',
      label: 'Present Date',
      value: format(new Date(), 'yyyy-MM-dd'),
    },
    {
      type: 'date',
      name: 'futureDate',
      label: 'Future Date',
      value: format(addYears(new Date(), 1), 'yyyy-MM-dd'),
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'currency',
      name: 'presentValue',
      label: 'Present Value',
      value: 10000,
    },
    {
      type: 'number',
      dataType: 'float',
      displayAs: 'percent',
      name: 'interestRate',
      label: 'Rate of Return',
      value: 6.9,
    },
    {
      type: 'dropdown',
      name: 'frequency',
      label: 'Compounding',
      value: 'monthly',
      options: [
        { name: 'Annual', value: 'annually' },
        { name: 'Semi-Annual', value: 'semi-annually' },
        { name: 'Quarterly', value: 'quarterly' },
        { name: 'Monthly', value: 'monthly' },
        { name: 'Weekly', value: 'weekly' },
        { name: 'Daily', value: 'daily' },
      ],
    },
  ],
}
